import { api } from '@goaltree/backend/convex/_generated/api'
import { CategoryBadge } from '@goaltree/ui/components/badge'
import { cn } from '@goaltree/ui/lib/utils'
import {
  Background,
  Controls,
  type Edge,
  Handle,
  MarkerType,
  type Node,
  type NodeProps,
  Position,
  ReactFlow,
  useNodesState,
} from '@xyflow/react'
import { useMutation } from 'convex/react'
import dagre from 'dagre'
import { Check } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useTodayTasks } from '@/hooks/use-today-tasks'
import { CATEGORY_COLORS, categoryTextColor, useCategoryFill } from '@/lib/category-color'
import { completeNode } from '@/lib/nodes-optimistic'

import type { GoalTreeData } from './goal-tree'
import { NodeStatusIndicator } from './node-status-indicator'

// ─── constants ───────────────────────────────────────────────────────────────

const EFFORT_CLASSES: Record<string, string> = {
  low: 'text-green-500 dark:text-green-400',
  medium: 'text-yellow-500 dark:text-yellow-400',
  high: 'text-red-500 dark:text-red-400',
}

const DIMS = {
  goal: { w: 280, h: 88 },
  milestone: { w: 220, h: 68 },
  target: { w: 200, h: 60 },
  task: { w: 176, h: 80 },
} as const

// ─── custom nodes ─────────────────────────────────────────────────────────────

function GoalFlowNode({ data }: NodeProps) {
  const { t } = useTranslation()
  const fill = useCategoryFill()
  const d = data as {
    title: string
    category: string
    progress: number
    taskCount: number
    completedCount: number
  }
  const status = d.taskCount > 0 && d.completedCount === d.taskCount ? 'success' : 'initial'
  const color = fill(CATEGORY_COLORS[d.category?.toLowerCase()] ?? '#6b7280')
  const pct = Math.round(Math.min(100, Math.max(0, d.progress)))

  return (
    <NodeStatusIndicator status={status}>
      <div
        className="overflow-hidden rounded-xl border border-border bg-card shadow-md"
        style={{ width: DIMS.goal.w }}
      >
        <div className="p-4">
          <div className="flex items-start gap-3">
            <p className="min-w-0 flex-1 text-sm font-semibold leading-snug line-clamp-2">
              {d.title}
            </p>
            <CategoryBadge category={d.category} label={t(`goals.cat.${d.category}`)} />
          </div>
          {d.taskCount > 0 && (
            <div className="mt-3 flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {d.taskCount - d.completedCount > 0
                  ? `${d.taskCount - d.completedCount} remaining`
                  : 'All tasks complete'}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {d.completedCount}/{d.taskCount} tasks
                </span>
                {d.completedCount < d.taskCount && (
                  <span
                    className="text-xs font-semibold"
                    style={{ color: categoryTextColor(color) }}
                  >
                    {pct}%
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
        {d.taskCount > 0 && (
          <div className="relative h-[3px] bg-muted">
            <div
              className="absolute left-0 top-0 h-full transition-[width] duration-500"
              style={{ width: `${pct}%`, backgroundColor: color, boxShadow: `0 0 8px ${color}` }}
            />
          </div>
        )}
        <Handle type="source" position={Position.Bottom} className="!bg-border !border-border" />
      </div>
    </NodeStatusIndicator>
  )
}

function MilestoneFlowNode({ data }: NodeProps) {
  const d = data as { title: string; completedCount: number; taskCount: number }
  const isDone = d.taskCount > 0 && d.completedCount === d.taskCount
  const status = isDone ? 'success' : 'initial'
  return (
    <NodeStatusIndicator status={status}>
      <div
        className="rounded-lg border-2 border-border bg-card p-3 shadow-sm"
        style={{ width: DIMS.milestone.w }}
      >
        <Handle type="target" position={Position.Top} className="!bg-border" />
        <p
          className={cn(
            'text-sm font-medium leading-snug',
            isDone && 'text-muted-foreground line-through',
          )}
        >
          {d.title}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {d.completedCount}/{d.taskCount} tasks
        </p>
        <Handle type="source" position={Position.Bottom} className="!bg-border" />
      </div>
    </NodeStatusIndicator>
  )
}

function TargetFlowNode({ data }: NodeProps) {
  const d = data as { title: string; completedCount: number; taskCount: number }
  const isDone = d.taskCount > 0 && d.completedCount === d.taskCount
  const status = isDone ? 'success' : 'initial'
  return (
    <NodeStatusIndicator status={status}>
      <div className="rounded-lg border bg-muted/40 p-3 shadow-sm" style={{ width: DIMS.target.w }}>
        <Handle type="target" position={Position.Top} className="!bg-border" />
        <p
          className={cn(
            'text-xs font-medium leading-snug',
            isDone ? 'text-muted-foreground line-through' : 'text-foreground',
          )}
        >
          {d.title}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {d.completedCount}/{d.taskCount}
        </p>
        <Handle type="source" position={Position.Bottom} className="!bg-border" />
      </div>
    </NodeStatusIndicator>
  )
}

function TaskFlowNode({ data }: NodeProps) {
  const { t } = useTranslation()
  const d = data as {
    title: string
    effort: 'low' | 'medium' | 'high'
    completedAt: number | null
    taskId: string
    isToday: boolean
  }
  const complete = useMutation(api.nodes.complete).withOptimisticUpdate((store, { taskId }) =>
    completeNode(store, taskId),
  )
  const [optimistic, setOptimistic] = useState(false)
  const isDone = optimistic || d.completedAt !== null
  const status = isDone ? 'success' : d.isToday ? 'loading' : 'initial'

  async function handleComplete() {
    if (isDone) return
    setOptimistic(true)
    try {
      await complete({ taskId: d.taskId as never })
    } catch {
      setOptimistic(false)
    }
  }

  return (
    <NodeStatusIndicator status={status}>
      <div
        className={cn('rounded-lg border bg-card p-3 shadow-sm', isDone && 'opacity-60')}
        style={{ width: DIMS.task.w }}
      >
        <Handle type="target" position={Position.Top} className="!bg-border" />
        <div className="flex items-start gap-2">
          <button
            type="button"
            onClick={handleComplete}
            disabled={isDone}
            className={cn(
              'mt-0.5 flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded-full border-2 transition-colors',
              isDone
                ? 'border-green-500 bg-green-500'
                : 'border-muted-foreground/40 hover:border-primary',
            )}
          >
            {isDone && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
          </button>
          <p className={cn('flex-1 text-xs font-medium leading-snug', isDone && 'line-through')}>
            {d.title}
          </p>
        </div>
        <span
          className={cn(
            'mt-2 inline-block text-xs font-medium',
            EFFORT_CLASSES[d.effort] ?? EFFORT_CLASSES.medium,
          )}
        >
          {t(`stats.effort${d.effort.charAt(0).toUpperCase() + d.effort.slice(1)}`)}
        </span>
      </div>
    </NodeStatusIndicator>
  )
}

const nodeTypes = {
  goal: GoalFlowNode,
  milestone: MilestoneFlowNode,
  target: TargetFlowNode,
  task: TaskFlowNode,
}

// ─── layout ──────────────────────────────────────────────────────────────────

function applyDagreLayout(nodes: Node[], edges: Edge[]): Node[] {
  const g = new dagre.graphlib.Graph()
  g.setDefaultEdgeLabel(() => ({}))
  g.setGraph({ rankdir: 'TB', nodesep: 32, ranksep: 100, marginx: 40, marginy: 40 })

  for (const node of nodes) {
    const dims = DIMS[node.type as keyof typeof DIMS] ?? DIMS.task
    g.setNode(node.id, { width: dims.w, height: dims.h })
  }
  for (const edge of edges) {
    g.setEdge(edge.source, edge.target)
  }

  dagre.layout(g)

  return nodes.map((node) => {
    const pos = g.node(node.id)
    const dims = DIMS[node.type as keyof typeof DIMS] ?? DIMS.task
    return { ...node, position: { x: pos.x - dims.w / 2, y: pos.y - dims.h / 2 } }
  })
}

// ─── tree → flow conversion ──────────────────────────────────────────────────

type NodeData = Record<string, unknown>

function buildNodeDataMap(
  tree: GoalTreeData,
  todayIds: Set<string> = new Set(),
): Map<string, NodeData> {
  const map = new Map<string, NodeData>()

  const allTasks = tree.milestones.flatMap((ms) => ms.targets.flatMap((tg) => tg.tasks))
  const completedCount = allTasks.filter((t) => t.completedAt !== null).length

  const cat = tree.category.toLowerCase()

  map.set(tree._id, {
    title: tree.title,
    category: cat,
    progress: tree.progress,
    taskCount: allTasks.length,
    completedCount,
  })

  for (const ms of tree.milestones) {
    const msTasks = ms.targets.flatMap((tg) => tg.tasks)
    const msCompleted = msTasks.filter((t) => t.completedAt !== null).length
    map.set(ms._id, { title: ms.title, taskCount: msTasks.length, completedCount: msCompleted })

    for (const tg of ms.targets) {
      const tgCompleted = tg.tasks.filter((t) => t.completedAt !== null).length
      map.set(tg._id, { title: tg.title, taskCount: tg.tasks.length, completedCount: tgCompleted })

      for (const task of tg.tasks) {
        map.set(task._id, {
          title: task.title,
          effort: task.effort,
          completedAt: task.completedAt,
          taskId: task._id,
          isToday: todayIds.has(task._id),
        })
      }
    }
  }

  return map
}

function treeToFlow(tree: GoalTreeData): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = []
  const edges: Edge[] = []

  const edgeDefaults = {
    markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16 },
  }

  const dataMap = buildNodeDataMap(tree)

  nodes.push({ id: tree._id, type: 'goal', position: { x: 0, y: 0 }, data: dataMap.get(tree._id)! })

  for (const ms of tree.milestones) {
    nodes.push({
      id: ms._id,
      type: 'milestone',
      position: { x: 0, y: 0 },
      data: dataMap.get(ms._id)!,
    })
    edges.push({ id: `${tree._id}→${ms._id}`, source: tree._id, target: ms._id, ...edgeDefaults })

    for (const tg of ms.targets) {
      nodes.push({
        id: tg._id,
        type: 'target',
        position: { x: 0, y: 0 },
        data: dataMap.get(tg._id)!,
      })
      edges.push({ id: `${ms._id}→${tg._id}`, source: ms._id, target: tg._id, ...edgeDefaults })

      for (const task of tg.tasks) {
        nodes.push({
          id: task._id,
          type: 'task',
          position: { x: 0, y: 0 },
          data: dataMap.get(task._id)!,
        })
        edges.push({
          id: `${tg._id}→${task._id}`,
          source: tg._id,
          target: task._id,
          ...edgeDefaults,
        })
      }
    }
  }

  return { nodes, edges }
}

// ─── main component ──────────────────────────────────────────────────────────

export default function GoalFlow({ tree }: { tree: GoalTreeData }) {
  const todayState = useTodayTasks()
  const todayIds = new Set<string>(
    todayState.status === 'tasks' ? todayState.tasks.map((t) => String(t.taskId)) : [],
  )
  // Stable string key so the data effect can depend on it without Set reference churn.
  const todayIdsKey = [...todayIds].sort().join(',')

  // Structural key — changes only when nodes are added or removed, not on content edits.
  const structureKey = [
    tree._id,
    ...tree.milestones.flatMap((ms) => [
      ms._id,
      ...ms.targets.flatMap((tg) => [tg._id, ...tg.tasks.map((t) => t._id)]),
    ]),
  ].join('|')

  const { edges } = treeToFlow(tree)
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([])

  // Recompute layout only when topology changes. Drag positions live inside React
  // Flow's internal store and are never reset by this effect.
  useEffect(() => {
    const { nodes: raw, edges: e } = treeToFlow(tree)
    setNodes(applyDagreLayout(raw, e))
  }, [structureKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // Update node data (completions, renames, today-flags) without touching positions.
  useEffect(() => {
    const dataMap = buildNodeDataMap(tree, todayIds)
    setNodes((prev) =>
      prev.map((n) => {
        const data = dataMap.get(n.id)
        return data ? { ...n, data } : n
      }),
    )
  }, [tree, todayIdsKey]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      nodeTypes={nodeTypes}
      fitView
      fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
      minZoom={0.05}
      maxZoom={1.5}
      panOnScroll
      className="bg-background"
    >
      <Background />
      <Controls />
    </ReactFlow>
  )
}
